import { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Download, Copy, Check, Instagram, AlertCircle, Square, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { getEventImage } from '@/lib/eventHelpers';
import { logError } from '@/lib/logger';
import { buildSocialCaption, copyToClipboard } from '@/lib/socialCaption';
import {
  renderIgCanvas,
  downloadCanvasAsPng,
  igFilenameFor,
} from '@/lib/igGraphicGenerator';

// ------ Sub-components (kept in the same file since they're small &
//         only used here; extraction just lowers the parent complexity) ------

function PreviewFrame({ loading, error, previewUrl, format }) {
  const aspectRatio = format === 'story' ? '9 / 16' : '1 / 1';
  return (
    <div
      className="relative rounded-lg overflow-hidden border border-[color:var(--border-c)] bg-black transition-all"
      style={{ aspectRatio }}
      data-testid="social-post-preview-frame"
      data-format={format}
    >
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Skeleton className="w-full h-full bg-[color:var(--surface-2)]" />
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-xs text-[color:var(--text-muted)]">
            <div className="w-6 h-6 rounded-full border-2 border-[color:var(--gold)] border-t-transparent animate-spin" />
            Generazione…
          </div>
        </div>
      )}
      {!loading && error && (
        <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center gap-2">
          <AlertCircle className="w-8 h-8 text-amber-400" />
          <p className="text-sm text-[color:var(--text-muted)]" data-testid="social-post-preview-error">{error}</p>
        </div>
      )}
      {!loading && !error && previewUrl && (
        <img
          src={previewUrl}
          alt="Anteprima locandina Instagram"
          className="w-full h-full object-contain"
          data-testid="social-post-preview-image"
        />
      )}
    </div>
  );
}

function FormatToggle({ value, onChange, disabled }) {
  return (
    <ToggleGroup
      type="single"
      value={value}
      onValueChange={(v) => v && onChange(v)}
      disabled={disabled}
      data-testid="social-post-format-toggle"
      className="w-full grid grid-cols-2 rounded-lg border border-[color:var(--border-c)] bg-[color:var(--surface-2)] p-1 gap-1"
    >
      <ToggleGroupItem
        value="feed"
        data-testid="social-post-format-feed"
        aria-label="Formato Feed 1080x1080"
        className="data-[state=on]:bg-[color:var(--gold-strong)] data-[state=on]:text-black text-[color:var(--text-muted)] hover:text-[color:var(--text)] rounded-md text-xs font-medium tracking-wide"
      >
        <Square className="w-3.5 h-3.5 mr-1.5" />
        1080×1080 (Feed)
      </ToggleGroupItem>
      <ToggleGroupItem
        value="story"
        data-testid="social-post-format-story"
        aria-label="Formato Story 1080x1920"
        className="data-[state=on]:bg-[color:var(--gold-strong)] data-[state=on]:text-black text-[color:var(--text-muted)] hover:text-[color:var(--text)] rounded-md text-xs font-medium tracking-wide"
      >
        <Smartphone className="w-3.5 h-3.5 mr-1.5" />
        1080×1920 (Story)
      </ToggleGroupItem>
    </ToggleGroup>
  );
}

function CopyButton({ copied, onClick, disabled }) {
  return (
    <Button
      data-testid="social-post-copy-btn"
      onClick={onClick}
      disabled={disabled}
      variant="outline"
      className="w-full border-[color:var(--gold)] text-[color:var(--gold)] hover:bg-[rgba(214,185,140,0.08)] disabled:opacity-50"
    >
      {copied
        ? (<><Check className="w-4 h-4 mr-2" />Copiato!</>)
        : (<><Copy className="w-4 h-4 mr-2" />Copia Testo</>)}
    </Button>
  );
}

// ------------------------------- Main modal -------------------------------

/**
 * Social Media Hub modal.
 * Renders an IG-story preview + copy-paste caption in a 2-column layout.
 *
 * Props:
 *   - open (bool)
 *   - onOpenChange (fn)
 *   - evento (object) — full evento from /admin/eventi
 *   - locale (object|null) — the venue for this evento
 */
export default function SocialPostModal({ open, onOpenChange, evento, locale }) {
  const [previewUrl, setPreviewUrl] = useState('');
  const [loadingImg, setLoadingImg] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [format, setFormat] = useState('feed'); // 'feed' = 1080x1080 default, 'story' = 1080x1920
  const canvasRef = useRef(null);
  const generationTokenRef = useRef(0);

  const caption = useMemo(
    () => (evento ? buildSocialCaption(evento, locale) : ''),
    [evento, locale],
  );

  // Reset to Feed default whenever a new event is opened (not on every format change)
  useEffect(() => {
    if (open && evento) setFormat('feed');
  }, [open, evento?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Kick off canvas rendering whenever the modal opens for a new event OR format changes
  useEffect(() => {
    if (!open || !evento) {
      setPreviewUrl('');
      setError('');
      setCopied(false);
      canvasRef.current = null;
      return;
    }

    const token = ++generationTokenRef.current;
    const imageUrl = getEventImage(evento, locale);
    if (!imageUrl) {
      setError("Nessuna immagine disponibile: aggiungi una locandina all'evento o un logo al locale.");
      setPreviewUrl('');
      canvasRef.current = null;
      return;
    }

    setLoadingImg(true);
    setError('');
    setPreviewUrl('');

    renderIgCanvas({
      titolo: evento.titolo,
      data: evento.data,
      orario: evento.orario,
      venueName: locale?.nome || '',
      imageUrl,
      format,
    })
      .then(({ canvas, previewDataUrl }) => {
        if (token !== generationTokenRef.current) return;
        canvasRef.current = canvas;
        setPreviewUrl(previewDataUrl);
      })
      .catch((err) => {
        if (token !== generationTokenRef.current) return;
        logError('IG preview render failed', err);
        setError(err?.message || 'Errore nella generazione della locandina');
      })
      .finally(() => {
        if (token === generationTokenRef.current) setLoadingImg(false);
      });
  }, [open, evento, locale, format]);

  const handleDownload = async () => {
    if (!canvasRef.current || !evento) return;
    try {
      await downloadCanvasAsPng(
        canvasRef.current,
        igFilenameFor({ titolo: evento.titolo, data: evento.data, format }),
      );
      toast.success('Immagine scaricata');
    } catch (err) {
      logError(err);
      toast.error('Errore durante il download');
    }
  };

  const handleCopy = async () => {
    if (!caption) return;
    try {
      await copyToClipboard(caption);
      setCopied(true);
      toast.success('Caption copiata negli appunti');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      logError(err);
      toast.error('Copia non riuscita');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="social-post-modal"
        className="max-w-4xl bg-[color:var(--surface)] border-[color:var(--border-c)] text-[color:var(--text)] p-0 overflow-hidden max-h-[92vh] flex flex-col"
      >
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-[color:var(--border-c)]">
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Instagram className="w-5 h-5 text-[color:var(--gold)]" />
            Social Media Hub
          </DialogTitle>
          <DialogDescription className="text-xs text-[color:var(--text-muted)]">
            Scarica la locandina e copia la caption pronta per Instagram. Scegli tra Feed (1:1) e Story (9:16).
          </DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-6 p-6 overflow-y-auto">
          {/* LEFT: preview + download */}
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs uppercase tracking-[0.18em] text-[color:var(--text-subtle)]">Anteprima</div>
              <div className="text-[10px] text-[color:var(--text-subtle)] tabular-nums" data-testid="social-post-current-format">
                {format === 'story' ? '1080 × 1920' : '1080 × 1080'}
              </div>
            </div>
            <FormatToggle value={format} onChange={setFormat} disabled={loadingImg} />
            <PreviewFrame loading={loadingImg} error={error} previewUrl={previewUrl} format={format} />
            <Button
              data-testid="social-post-download-btn"
              onClick={handleDownload}
              disabled={!canvasRef.current || loadingImg}
              className="w-full bg-[color:var(--gold-strong)] hover:bg-[#C6A21A] text-black font-semibold disabled:opacity-50"
            >
              <Download className="w-4 h-4 mr-2" />
              Scarica Immagine
            </Button>
          </div>

          {/* RIGHT: caption + copy */}
          <div className="flex flex-col gap-3">
            <div className="text-xs uppercase tracking-[0.18em] text-[color:var(--text-subtle)]">Caption pronta</div>
            <Textarea
              data-testid="social-post-caption"
              value={caption}
              readOnly
              rows={16}
              onFocus={(e) => e.target.select()}
              className="flex-1 min-h-[360px] bg-[color:var(--surface-2)] border-[color:var(--border-c)] text-sm leading-relaxed font-mono resize-none focus-visible:ring-1 focus-visible:ring-[color:var(--gold)]"
            />
            <CopyButton copied={copied} onClick={handleCopy} disabled={!caption} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
